import { match } from 'ts-pattern';
import type { MediaType } from '~/graphql';

// allowed mime types should be kept in sync with https://support.wistia.com/en/articles/8268259-export-settings#h_f0483feacf
// and https://github.com/wistia/granary/blob/master/internal/pkg/media/attributes.go
const acceptedVideoFileExtensions = [
  'mov',
  'mpg',
  'avi',
  'flv',
  'f4v',
  'mp4',
  'm4v',
  'asf',
  'wmv',
  'vob',
  'mod',
  '3gp',
  'mkv',
  'divx',
  'xvid',
  'webm',
  'mxf',
] as const;

const acceptedAudioFileExtensions = ['mp3', 'wav', 'aif', 'au', 'm4a', 'ogg'] as const;

const acceptedImageFileExtensions = ['jpg', 'jpeg', 'png', 'tiff', 'bmp', 'dcm'] as const;

const acceptedMicrosoftOfficeDocumentFileExtensions = [
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
] as const;

export const acceptedPdfDocumentFileExtensions = ['pdf'] as const;

const acceptedDocumentFileExtensions = [
  ...acceptedPdfDocumentFileExtensions,
  ...acceptedMicrosoftOfficeDocumentFileExtensions,
] as const;

export const allAcceptedFontFileExtensions = ['woff', 'woff2', 'ttf', 'otf'] as const;

const acceptedAudioAndVideoFileExtensionsForUploader = [
  ...acceptedAudioFileExtensions,
  ...acceptedVideoFileExtensions,
];

const allAcceptedFileExtensionsForUploader = [
  ...acceptedVideoFileExtensions,
  ...acceptedAudioFileExtensions,
  ...acceptedImageFileExtensions,
  ...acceptedDocumentFileExtensions,
];

const allAcceptedFileExtensionsForProject = [
  ...acceptedVideoFileExtensions,
  ...acceptedAudioFileExtensions,
  ...acceptedImageFileExtensions,
  ...acceptedDocumentFileExtensions,
];

export const formattedAcceptedFileExtensionsForProject = [...allAcceptedFileExtensionsForProject]
  .map((ext) => `.${ext}`)
  .join(', ');

export const formattedAcceptedAudioAndVideoFileExtensions = [
  ...acceptedAudioAndVideoFileExtensionsForUploader,
]
  .map((ext) => `.${ext}`)
  .join(', ');

export const formattedAllAcceptedFileExtensions = [...allAcceptedFileExtensionsForUploader]
  .map((ext) => `.${ext}`)
  .join(', ');

export const formattedAllAcceptedImageFileExtensions = [...acceptedImageFileExtensions]
  .map((ext) => `.${ext}`)
  .join(', ');

export const formattedAllAcceptedFontFileExtensions = [...allAcceptedFontFileExtensions]
  .map((ext) => `.${ext}`)
  .join(', ');

export const formattedAcceptedFileExtensionsByCurrentMediaType = (
  currentMediaType: MediaType,
): string =>
  match(currentMediaType)
    .with('Video', () => acceptedVideoFileExtensions)
    .with('Audio', () => acceptedAudioFileExtensions)
    .with('Image', () => acceptedImageFileExtensions)
    .with('PdfDocument', () => acceptedPdfDocumentFileExtensions)
    .with('MicrosoftOfficeDocument', () => acceptedMicrosoftOfficeDocumentFileExtensions)
    .with('Document', () => acceptedDocumentFileExtensions)
    .otherwise(() => [])
    .map((ext: string) => `.${ext}`)
    .join(', ');
